import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useRef, useState } from "react";
import axios from "../../../api/axios";
import toast from "../../sweetalert/Sweetalert";
import { destroySession } from "../../../helpers/Session";
import { useNavigate } from "react-router-dom";
import axiosaim from "../../../api/axiosaim";

const AdjustmentRequest = ({ departmentname, departmentslug, employeeUname }) => {
    const APPROVER_TYPE = 2;

    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const timeoutRef = useRef(null);

    const accesstoken = sessionStorage.getItem('accesstoken');
    const aimtoken = sessionStorage.getItem('aimtoken');
    const creator = sessionStorage.getItem('uname');

    const [employeeList, setEmployeeList] = useState([]);
    const [loadingAdjustmentRequestApprovers, setLoadingAdjustmentRequestApprovers] = useState(false);

    const APPROVER_QUERY_KEY = ['departmentAdjustmentRequestApprovers', { departmentslug, APPROVER_TYPE, employeeUname }];

    const { data: employees } = useQuery({
        queryKey: ['departmentEmployees', {departmentslug}],
        queryFn: async () => {
            const res = await axiosaim.get(`getuserlistsperdepartment?d=${departmentslug}`, {
                headers: { 'X-Aim-Token': aimtoken }
            });
            return res.data;
        },
        enabled: !!departmentslug && !!aimtoken,
    });

    const { data: approvers, isLoading: isLoadingAdjustmentRequestApprovers, error: errorAdjustmentRequestApprovers } = useQuery({
        queryKey: APPROVER_QUERY_KEY,
        queryFn: async () => {
            if (!employeeUname) return [];
            try {
                const res = await axios.get(`approvals?department=${departmentslug}&type=${APPROVER_TYPE}&employee=${employeeUname}`, {
                    headers: { 'Authorization': `Bearer ${accesstoken}` }
                });
                setLoadingAdjustmentRequestApprovers(false);
                return res.data.response;
            } catch (error) {
                setLoadingAdjustmentRequestApprovers(false);
            }
        },
        onError: (error) => {
            setLoadingAdjustmentRequestApprovers(false);
        },
        onSuccess: () => {
            setLoadingAdjustmentRequestApprovers(false);
        },
        enabled: !!departmentslug && !!accesstoken,
        staleTime: 0,
        retry: false
    });
    
    useEffect(() => {
        if (approvers && approvers.length > 0) {
            setEmployeeList(approvers);
        } else {
            setEmployeeList([]);
        }
    }, [approvers]);
    
    useEffect(() => {
        if (errorAdjustmentRequestApprovers) {
            if (errorAdjustmentRequestApprovers.response?.status === 404) {
                setEmployeeList([]);
            } else {
                setEmployeeList([]);
            }
        }
    }, [errorAdjustmentRequestApprovers]);

    const handleAddApprover = () => {
        const newApprover = {
            id: `approver-${employeeList?.length + 1}`,
            sequence: employeeList?.length + 1,
            uname: `approver-${employeeList?.length + 1}`,
            fname: '',
            lname: '',
            email: ''
        };
        setEmployeeList(prevState => [...prevState, newApprover]);
    };

    const departmentAdjustmentRequestApproverMutation = useMutation({
        mutationFn: async (approverData) => {
            const res = await axios.post('approvals', approverData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data.response });
            queryClient.invalidateQueries(APPROVER_QUERY_KEY);
        },
        onError: (error) => {
            const errorMessage = error.response?.data.response || "Something went wrong while saving adjustment request approver details!";
            toast.fire({ icon: 'error', title: errorMessage });

            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleSetApprover = (e, id) => {
        const uname = e.target.value;

        if (uname === "") {
            setEmployeeList(prevState => prevState.filter(item => item.id = id));
        } else {
            const selectedEmployee = employees?.find(item => item.uname === uname);
            if (selectedEmployee) {
                const updatedEmployeeList = employeeList?.map(item => (
                    item.id === id 
                        ? {...item, ...selectedEmployee}
                        : item
                ));
                setEmployeeList(updatedEmployeeList);
                
                const employee = updatedEmployeeList?.find(item => item.id === id);
                departmentAdjustmentRequestApproverMutation.mutate({
                    id: employee.id,
                    department: departmentname,
                    departmentcode: departmentslug,
                    uname: employee.uname,
                    name: `${employee.fname} ${employee.lname}`,
                    email: employee.email,
                    sequence: employee.sequence,
                    status: 1,
                    type: APPROVER_TYPE,
                    creator,
                    employee: employeeUname
                });
            }
        }
    }

    const departmentApproverDeleteMutation = useMutation({
        mutationFn: async (id) => {
            const res = await axios.delete(`approvals/${id}?uname=${creator}&employee=${employeeUname}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data.response });
            queryClient.invalidateQueries(APPROVER_QUERY_KEY);
        },
        onError: (error) => {
            const errorMessage = error.response?.data.response || "Something went wrong while deleting timesheet approver details!";
            toast.fire({ icon: 'error', title: errorMessage });

            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });

    const handleRemoveEmployee = (uname) => {
        const employee = employeeList?.find(item => item.uname === uname);

        if (employee && employee.fname !== "") {
            departmentApproverDeleteMutation.mutate(employee.id, creator);
            setEmployeeList(prevState => prevState.filter(emp => emp.uname !== uname));
        }
    };

    return (
        <div className="content">
            {isLoadingAdjustmentRequestApprovers || loadingAdjustmentRequestApprovers ? (
                [...Array(2)].map((_, i) => (
                    <div key={i} className="employee loading">
                        <select>
                            <option value="ralejo">Resty Alejo</option>
                        </select>
                    </div>
                ))
            ) : (
                <>
                    {employeeList?.map((item, index) => (
                        <div key={index} className="employee">
                            <select value={item.uname} onChange={(e) => handleSetApprover(e, item.id)}>
                                {employees?.length > 0 ? 
                                    (
                                        <>
                                            <option value=""></option>
                                            {employees?.reduce((acc, { department, uname, fname, lname, position_title, employee_id }) => {
                                                if (!acc[department]) acc[department] = [];
                                                acc[department].push({ uname, fname, lname, position_title, employee_id });
                                                return acc;
                                            }, {}) && Object.entries(employees?.reduce((acc, { department, uname, fname, lname, position_title, employee_id }) => {
                                                if (!acc[department]) acc[department] = [];
                                                acc[department].push({ uname, fname, lname, position_title, employee_id });
                                                return acc;
                                            }, {})).map(([department, employees]) => (
                                                <optgroup key={department} label={department}>
                                                {employees.map(({ uname, fname, lname }) => (
                                                    <option key={uname} value={uname}>
                                                    {`${fname} ${lname}`}
                                                    </option>
                                                ))}
                                                </optgroup>
                                            ))}
                                        </>
                                    ) : (
                                        employeeList?.length > 0 && (
                                            <option key={item.uname} value={item.uname}>
                                                {item.name}
                                            </option>
                                        )
                                    )
                                }
                                    
                                {employeeList?.length > 0 && item?.length > 0 && !employees.some(emp => emp.uname === item.uname) && (
                                    <option key={item.uname} value={item.uname}>
                                        {item.name}
                                    </option>
                                )}
                            </select>
                            {index > 0 && (
                                <button type="button" className="btn-remove-employee" title="REMOVE" onClick={() => handleRemoveEmployee(item.uname)}>
                                    <i className="fa fa-trash"></i>
                                </button>
                            )}
                        </div>
                    ))}

                    <div className="button-group">
                        {employeeList?.length < employees?.length && (
                            <button type="button" className="btn-add-employee" onClick={handleAddApprover}>
                                <i className="fa fa-plus"></i>
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    )
}

export default AdjustmentRequest;